const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const toNumeric = (value: unknown): number | null => {
  if (isFiniteNumber(value)) {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

type BalanceRecordLike = Record<string, unknown> | null | undefined;

const sumBalanceField = (
  balances: Record<string, { balance?: unknown } | undefined>,
  accountNumbers: string[],
  field: string,
): number | null => {
  if (accountNumbers.length === 0) {
    return null;
  }

  let total = 0;
  let hasValue = false;

  accountNumbers.forEach((accountNumber) => {
    const entry = balances[accountNumber];
    const balanceRecord = entry?.balance as BalanceRecordLike;
    if (!balanceRecord) {
      return;
    }
    const raw = (balanceRecord as Record<string, unknown>)[field];
    const numeric = toNumeric(raw);
    if (numeric !== null) {
      total += numeric;
      hasValue = true;
    }
  });

  return hasValue ? total : null;
};

export function sumNetLiquidityForAccounts(
  balances: Record<string, { balance?: unknown } | undefined>,
  accountNumbers: string[],
): number | null {
  return sumBalanceField(balances, accountNumbers, "net-liquidating-value");
}

export function sumCashBalanceForAccounts(
  balances: Record<string, { balance?: unknown } | undefined>,
  accountNumbers: string[],
): number {
  return sumBalanceField(balances, accountNumbers, "cash-balance") ?? 0;
}

export interface NetOffsetState {
  accountsKey: string;
  offset: number;
}

export function updateNetOffsetState(
  previous: NetOffsetState | null,
  accountsKey: string,
  aggregateNetLiq: number | null,
  rawNetLiq: number,
  jitterThreshold = 1,
): NetOffsetState | null {
  if (!accountsKey) {
    return null;
  }

  if (!isFiniteNumber(aggregateNetLiq)) {
    return previous && previous.accountsKey === accountsKey ? previous : null;
  }

  const offset = aggregateNetLiq - rawNetLiq;

  if (
    !previous ||
    previous.accountsKey !== accountsKey ||
    Math.abs(offset - previous.offset) > jitterThreshold
  ) {
    return {
      accountsKey,
      offset,
    };
  }

  return previous;
}

export function computeLiveNetLiq(
  rawNetLiq: number,
  baseline: NetOffsetState | null,
): number {
  const offset = baseline?.offset ?? 0;
  return rawNetLiq + offset;
}
